import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { endOfDay, startOfDay } from "date-fns";
import { PropTypes } from "prop-types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { identifiersData } from "@/data";
import { openAlert } from "@/redux/actions";
import { StyledDatePicker, StyledDateTimePicker } from "@/styles";
import { displayErrorHandler } from "@/utils";

const Calendar = ({
    allowedFrom,
    allowedTo,
    disablePast,
    errors,
    field,
    hasSmallField,
    hasTime,
    isFilterInput,
    isOptional,
    label,
    name,
    onChange,
    value,
    withoutLabelTranslation,
}) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    let inputLabel = "";

    const { classNames, ids } = identifiersData;
    const { smallField: smallFieldClassName, withoutPosition: withoutPositionClassName } = classNames;
    const { input: inputId } = ids;

    useEffect(
        () => {
            if (isFilterInput && !!errors?.[name]) {
                displayErrorHandler(errors?.[name]?.message, dispatch, openAlert);
            }
        },
        [errors], //eslint-disable-line
    );

    if (withoutLabelTranslation) inputLabel = label;
    else inputLabel = t(`labels.${label}`);

    const textFieldSlotProps = {
        className: hasSmallField && `${smallFieldClassName} ${withoutPositionClassName}`,
        error: !!errors?.[name],
        fullWidth: true,
        helperText: errors?.[name] && !isFilterInput && errors?.[name]?.message,
        id: `${name}_${inputId}`,
        isCalendar: +true,
        name,
    };

    if (hasTime) {
        return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <StyledDateTimePicker
                    disablePast={disablePast ?? false}
                    enableAccessibleFieldDOMStructure={false}
                    format="dd/MM/yyyy hh:mm a"
                    hasSmallField={hasSmallField}
                    label={isOptional ? `${inputLabel} (${t("labels.optional")})` : inputLabel}
                    maxDateTime={allowedTo ?? undefined}
                    minDateTime={allowedFrom ?? undefined}
                    slotProps={{
                        textField: textFieldSlotProps,
                    }}
                    {...field}
                    {...(onChange && { onChange })}
                    value={value}
                />
            </LocalizationProvider>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StyledDatePicker
                disablePast={disablePast ?? false}
                enableAccessibleFieldDOMStructure={false}
                format="dd/MM/yyyy"
                hasSmallField={hasSmallField}
                label={isOptional ? `${inputLabel} (${t("labels.optional")})` : inputLabel}
                maxDate={allowedTo ? endOfDay(allowedTo) : undefined}
                minDate={allowedFrom ? startOfDay(allowedFrom) : undefined}
                slotProps={{
                    popper: {
                        placement: "auto",
                    },
                    textField: textFieldSlotProps,
                }}
                {...field}
                {...(onChange && { onChange })}
                value={value}
            />
        </LocalizationProvider>
    );
};

export default Calendar;

Calendar.propTypes = {
    allowedFrom: PropTypes.instanceOf(Date),
    allowedTo: PropTypes.instanceOf(Date),
    disablePast: PropTypes.bool,
    errors: PropTypes.object,
    field: PropTypes.object,
    hasSmallField: PropTypes.bool,
    hasTime: PropTypes.bool,
    isFilterInput: PropTypes.bool,
    isOptional: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    withoutLabelTranslation: PropTypes.bool,
};
